import { rankOf, type TenantRole } from '@esa/shared';
import { forwardRef, useState, type ReactNode } from 'react';
import { AlertTriangle, Plus } from 'lucide-react';
import type { TenantConfig } from '../../lib/api.js';
import {
  Button,
  Callout,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Field,
  Input,
  Select,
  Checkbox,
  Switch,
  Textarea,
  type ButtonProps,
} from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * The editing half of the Configuration screen.
 *
 * Each form is a dialog over the same primitives, and each one closes only
 * once the write has actually landed — a dialog that closes optimistically and
 * then toasts a failure leaves the user staring at stale values wondering
 * which of the two they should believe.
 *
 * Keys are immutable once created. The key is the identity: renaming it would
 * orphan every ticket, rule and report that references it, so the forms offer
 * the label for editing and show the key read-only.
 */

// ---------------------------------------------------------------------------
// Shared scaffolding
// ---------------------------------------------------------------------------

function FormDialog({
  trigger,
  title,
  description,
  submitLabel,
  onSubmit,
  pending,
  width = 'md',
  children,
  disabled,
  onOpen,
}: {
  trigger: ReactNode;
  title: string;
  description?: ReactNode;
  submitLabel: string;
  onSubmit: () => Promise<unknown>;
  pending: boolean;
  width?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  disabled?: boolean;
  /** Called as the dialog opens: the place to fill the fields from what is saved NOW. */
  onOpen?: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) onOpen?.();
        setOpen(o);
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent title={title} description={description} width={width}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            // Close on success only. The mutation's own toast reports failure.
            void onSubmit().then(
              () => setOpen(false),
              () => undefined,
            );
          }}
        >
          <div className="space-y-3">{children}</div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" loading={pending} disabled={disabled}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** A destructive action behind one confirmation, with the reason spelled out. *//**
 * What saving the field form sends: a NEW VERSION of the field.
 *
 * EVERYTHING THE FORM DOES NOT SHOW CARRIES OVER. The form edits the label,
 * type, options and a few settings; it does not show help text, show-when,
 * order or other validation. Those were reset by omission -- and the options
 * box opened empty, so saving a select field wrote a version with no options
 * at all. What is not on the form is taken from the version being replaced.
 */
export function fieldSaveBody(
  existing:
    | {
        validation?: Record<string, unknown>;
        help_text?: string | null;
        show_when?: { field: string; equals: unknown } | null;
        sort_order?: number;
      }
    | undefined,
  form: {
    key: string;
    label: string;
    fieldType: string;
    requiredAt: string;
    visibility: string;
    whenMissing: string;
    consumer: string;
    options: string;
    types: string;
  },
): Record<string, unknown> {
  const nl = String.fromCharCode(10);
  const needsOptions = form.fieldType === 'select' || form.fieldType === 'multiselect';
  const { options: _old, ...otherValidation } = (existing?.validation ?? {}) as Record<string, unknown>;
  return {
    key: form.key,
    label: form.label.trim(),
    fieldType: form.fieldType,
    requiredAt: form.requiredAt,
    visibility: form.visibility,
    whenMissingByEmail: form.whenMissing,
    consumer: form.consumer.trim(),
    validation: {
      ...otherValidation,
      ...(needsOptions
        ? {
            options: form.options
              .split(nl)
              .map((o) => o.trim())
              .filter(Boolean),
          }
        : {}),
    },
    ...(existing?.help_text ? { helpText: existing.help_text } : {}),
    ...(existing?.show_when ? { showWhen: existing.show_when } : {}),
    sortOrder: existing?.sort_order ?? 0,
    requestTypeKeys:
      form.types.trim().length === 0
        ? null
        : form.types
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean),
  };
}


export function ConfirmAction({
  trigger,
  title,
  body,
  confirmLabel,
  onConfirm,
  pending,
}: {
  trigger: ReactNode;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<unknown>;
  pending: boolean;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent title={title} width="sm">
        <div className="text-xs leading-relaxed text-ink-secondary">{body}</div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="ghost">
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            loading={pending}
            onClick={() =>
              void onConfirm().then(
                () => setOpen(false),
                () => undefined,
              )
            }
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The "New ..." trigger.
 *
 * MUST forward its ref and spread its props. Every one of these is used as a
 * Radix `asChild` trigger, and Slot works by cloning the child with the
 * trigger's own onClick, ref and aria-* attached. A wrapper that reads only
 * `children` silently drops all of it, and the button renders perfectly while
 * doing nothing at all — which is exactly how it shipped.
 */
export const NewButton = forwardRef<HTMLButtonElement, ButtonProps>(function NewButton(
  { children, ...rest },
  ref,
) {
  return (
    <Button ref={ref} size="sm" variant="secondary" {...rest}>
      <Plus aria-hidden className="h-3.5 w-3.5" />
      {children}
    </Button>
  );
});

const num = (v: string, fallback = 0): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

// ---------------------------------------------------------------------------
// Queues
// ---------------------------------------------------------------------------

export function QueueForm({
  tenant,
  existing,
  trigger,
  agents,
}: {
  tenant: string;
  existing?: {
    key: string;
    name: string;
    description?: string | null;
    isDefault: boolean;
    restricted?: boolean;
    restrictedScope?: string | null;
    autoAssign?: 'off' | 'in_turn' | 'least_open';
    autoAssignPool?: string[] | null;
  };
  trigger: ReactNode;
  /** The group's staff, for choosing who new work is handed to. */
  agents?: readonly { oid: string; name: string | null; email: string }[];
}) {
  const write = useConfigWrite(tenant);
  const [key, setKey] = useState(existing?.key ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  // From the saved queue: this started empty, so saving a queue -- even
  // just to rename it -- wiped its description. It is also the one place a
  // queue says what it is for, which routing reads; a restricted queue saved
  // with the older separate box carries that over here.
  const [description, setDescription] = useState(existing?.description ?? existing?.restrictedScope ?? '');
  const [isDefault, setIsDefault] = useState(existing?.isDefault ?? false);
  const [restricted, setRestricted] = useState(existing?.restricted ?? false);
  const [autoAssign, setAutoAssign] = useState<'off' | 'in_turn' | 'least_open'>(existing?.autoAssign ?? 'off');
  // Empty = everyone who is an agent or queue lead here.
  const [pool, setPool] = useState<string[]>(existing?.autoAssignPool ?? []);
  const keyOk = /^[a-z][a-z0-9_]{1,40}$/.test(key);

  return (
    <FormDialog
      trigger={trigger}
      title={existing ? `Queue · ${existing.key}` : 'New queue'}
      description="A queue is where routing lands work. Every tenant needs exactly one default — the place a ticket goes when no rule claims it."
      submitLabel={existing ? 'Save queue' : 'Create queue'}
      pending={write.isPending}
      disabled={!keyOk || name.trim().length === 0 || (restricted && isDefault)}
      onSubmit={() =>
        write.mutateAsync({
          method: 'post',
          path: `/api/v1/tenants/${tenant}/admin/queues`,
          body: {
            key,
            name: name.trim(),
            description: description.trim() || null,
            isDefault,
            sortOrder: 0,
            restricted,
            // Folded into the description, which is what routing reads.
            restrictedScope: null,
            autoAssign,
            autoAssignPool: pool.length ? pool : null,
          },
          done: existing ? `Queue "${name}" saved` : `Queue "${name}" created`,
        })
      }
    >
      <Field
        label="Key"
        hint={
          existing
            ? 'The key is the identity — renaming it would orphan every ticket and rule that references it'
            : 'Lowercase letters, digits and underscores. Cannot be changed later.'
        }
        error={key && !keyOk ? 'Lowercase letters, digits and underscores only' : undefined}
        required
      >
        <Input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={Boolean(existing)}
          placeholder="accounts"
        />
      </Field>
      <Field label="Name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Accounts & Access" />
      </Field>
      <Field
        label="What this queue is for"
        hint="Requests about this are routed here automatically, from email and the portal: the assistant reads every request against every queue's description. Describe the subjects in plain words. Also shown to agents choosing where to move a ticket."
      >
        <Textarea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Payroll, expenses and reimbursements; questions about a payslip."
        />
      </Field>
      <Switch
        checked={isDefault}
        onCheckedChange={setIsDefault}
        label="This is the default queue"
      />
      <Field
        label="Hand new tickets out automatically"
        hint="Applies to a new ticket that lands here unassigned, and to one moved or re-routed here. Never to one the assistant answered, one waiting for a person to route it, or one somebody already holds."
      >
        <Select
          value={autoAssign}
          onValueChange={(v) => setAutoAssign(v as 'off' | 'in_turn' | 'least_open')}
          options={[
            { value: 'off', label: 'Off — people pick work up themselves' },
            { value: 'in_turn', label: 'In turn — each person gets the next one' },
            { value: 'least_open', label: 'To whoever has the fewest open tickets' },
          ]}
          aria-label="Hand new tickets out automatically"
        />
      </Field>
      {autoAssign !== 'off' && (agents?.length ?? 0) > 0 && (
        <Field
          label="To these people"
          hint={
            restricted
              ? 'Only members of this restricted queue are ever given its tickets, whoever is ticked here.'
              : 'Leave all unticked for every agent and queue lead in the group.'
          }
        >
          <div className="grid max-h-48 gap-1.5 overflow-y-auto rounded-md border border-line-subtle p-2.5 sm:grid-cols-2">
            {agents!.map((a) => (
              <Checkbox
                key={a.oid}
                checked={pool.includes(a.oid)}
                onCheckedChange={(on) => setPool((cur) => (on ? [...cur, a.oid] : cur.filter((x) => x !== a.oid)))}
                label={a.name ?? a.email}
              />
            ))}
          </div>
        </Field>
      )}
      <Switch
        checked={restricted}
        onCheckedChange={setRestricted}
        disabled={isDefault}
        label="Restricted — only its members can see its tickets"
      />
      {isDefault && <p className="text-2xs text-ink-tertiary">The default queue cannot be restricted: routing needs somewhere everyone can see.</p>}
      {restricted && (
        <>
          <Callout tone="warn" icon={<AlertTriangle />}>
            Its tickets disappear for everyone who is not a member — other agents, and administrators too unless they
            add themselves. Add members with the people button on the queue list after saving. Requesters always
            see their own tickets. Requests about what this queue is for are routed here first, whatever the routing
            rules say.
          </Callout>
        </>
      )}
      {isDefault && !existing?.isDefault && (
        <Callout tone="warn" icon={<AlertTriangle />}>
          Making this the default moves the flag off whichever queue holds it now. Only one queue
          per tenant can be the default.
        </Callout>
      )}
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Event types ("Move to Event")
// ---------------------------------------------------------------------------

export function EventTypeForm({
  tenant,
  existing,
  trigger,
}: {
  tenant: string;
  existing?: { key: string; name: string; description: string | null; defaultDays: number | null };
  trigger: ReactNode;
}) {
  const write = useConfigWrite(tenant);
  const [key, setKey] = useState(existing?.key ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [defaultDays, setDefaultDays] = useState(
    existing?.defaultDays === null || existing?.defaultDays === undefined
      ? ''
      : String(existing.defaultDays),
  );
  const keyOk = /^[a-z][a-z0-9_]{1,40}$/.test(key);

  return (
    <FormDialog
      trigger={trigger}
      title={existing ? `Event · ${existing.key}` : 'New event'}
      description="An event is a dated real-world thing a ticket can wait on — a trip, a start date, a maintenance window. The agent sets the actual return date; this is only the catalogue of what a desk waits for."
      submitLabel={existing ? 'Save event' : 'Create event'}
      pending={write.isPending}
      disabled={!keyOk || name.trim().length === 0}
      onSubmit={() =>
        write.mutateAsync({
          method: 'post',
          path: `/api/v1/tenants/${tenant}/admin/event-types`,
          body: {
            key,
            name: name.trim(),
            description: description.trim() || null,
            defaultDays: defaultDays.trim() === '' ? null : num(defaultDays),
            sortOrder: 0,
            active: true,
          },
          done: existing ? `Event "${name}" saved` : `Event "${name}" created`,
        })
      }
    >
      <Field
        label="Key"
        hint={
          existing
            ? 'The key is the identity — renaming it would orphan every ticket parked on it'
            : 'Lowercase letters, digits and underscores. Cannot be changed later.'
        }
        error={key && !keyOk ? 'Lowercase letters, digits and underscores only' : undefined}
        required
      >
        <Input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={Boolean(existing)}
          placeholder="international_travel_return"
        />
      </Field>
      <Field label="Name" required>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="International travel return" />
      </Field>
      <Field label="Description" hint="Shown to agents choosing what to park a ticket on">
        <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <Field label="Suggested wait" hint="Days. Leave blank if there is no typical length.">
        <Input
          type="number"
          min="1"
          max="3650"
          value={defaultDays}
          onChange={(e) => setDefaultDays(e.target.value)}
          className="w-28"
          placeholder="no default"
        />
      </Field>
      <p className="text-2xs leading-relaxed text-ink-tertiary">
        This only prefills the return date in the "Move to Event" dialog. The agent can always
        change it — the useful cases are the ones where the length isn't fixed.
      </p>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Request types
// ---------------------------------------------------------------------------

export function RequestTypeForm({
  tenant,
  existing,
  trigger,
  nextSortOrder,
}: {
  tenant: string;
  existing?: {
    key: string;
    label: string;
    sortOrder: number;
    measuredPurity: number | null;
    description?: string | null;
    acknowledgement?: string | null;
  };
  trigger: ReactNode;
  nextSortOrder: number;
}) {
  const write = useConfigWrite(tenant);
  const [key, setKey] = useState(existing?.key ?? '');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [acknowledgement, setAcknowledgement] = useState(existing?.acknowledgement ?? '');
  const [sortOrder, setSortOrder] = useState(String(existing?.sortOrder ?? nextSortOrder));
  const keyOk = /^[a-z][a-z0-9_]{1,60}$/.test(key);

  return (
    <FormDialog
      trigger={trigger}
      title={existing ? `Request type · ${existing.key}` : 'New request type'}
      description="Order is the classification ladder's evaluation priority: the lowest number is tried first, so the most specific types belong at the top and catch-alls at the bottom."
      submitLabel={existing ? 'Save type' : 'Create type'}
      pending={write.isPending}
      disabled={!keyOk || label.trim().length === 0}
      onSubmit={() =>
        write.mutateAsync({
          method: 'post',
          path: `/api/v1/tenants/${tenant}/admin/request-types`,
          body: {
            key,
            label: label.trim(),
            sortOrder: num(sortOrder),
            // Purity is measured from the record, never typed in. A new type
            // has no measurement, which is not the same as a measurement of
            // zero, and the router treats the two differently.
            measuredPurity: existing?.measuredPurity ?? null,
            description: description.trim() || null,
            acknowledgement: acknowledgement.trim() || null,
          },
          done: existing ? `"${label}" saved` : `"${label}" created`,
        })
      }
    >
      <Field
        label="Key"
        error={key && !keyOk ? 'Lowercase letters, digits and underscores only' : undefined}
        required
      >
        <Input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={Boolean(existing)}
          placeholder="password_reset"
        />
      </Field>
      <Field label="Label" required>
        <Input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Password reset / MFA / lockout"
        />
      </Field>
      <Field
        label="What belongs here"
        hint="The assistant reads this to choose the type by meaning, for every new request. Describe situations, not keywords. Left empty, only the keyword ladder can pick this type."
      >
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          maxLength={1000}
          placeholder="Suspected phishing or scam: suspicious emails, links or attachments, and calls or Teams calls from someone claiming to be IT or support, especially asking for remote access, passwords or codes."
        />
      </Field>
      <Field
        label="Its own acknowledgement"
        hint="Replaces the desk's acknowledgement for new email tickets of this type, for example extra instructions on a security report. {ticket} becomes the ticket number. Markdown works. Leave empty to use the desk's (Configuration, Intake)."
      >
        <Textarea
          value={acknowledgement}
          onChange={(e) => setAcknowledgement(e.target.value)}
          rows={5}
          maxLength={3000}
          placeholder={"Thanks for reporting this. It is logged as {ticket} and a person on the desk will look at it today.\n\n**In the meantime:** do not reply to the sender, click links or install anything. If you already did, call the desk now."}
        />
      </Field>
      <Field label="Ladder order" hint="Lower is evaluated first" required>
        <Input
          type="number"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="w-28"
        />
      </Field>
      {existing && (
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          Measured purity{' '}
          {existing.measuredPurity === null
            ? 'has never been measured for this type'
            : `is ${Math.round(existing.measuredPurity * 100)}%, from the six-month record`}
          . It is not editable here — it is a measurement, not a setting.
        </p>
      )}
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Field definitions
// ---------------------------------------------------------------------------

const FIELD_TYPES = [
  'text',
  'longtext',
  'number',
  'date',
  'datetime',
  'select',
  'multiselect',
  'checkbox',
  'user',
  'project_number',
  'email',
  'url',
  'file',
] as const;

export function FieldDefForm({
  tenant,
  config,
  existing,
  trigger,
}: {
  tenant: string;
  config: TenantConfig | undefined;
  existing?: {
    key: string;
    label: string;
    field_type: string;
    required_at: string;
    visibility: string;
    consumer: string;
    request_type_keys: string[] | null;
    version: number;
    when_missing_by_email?: string;
    /** Carried into the next version as they are; only `options` is edited here. */
    validation?: { options?: string[] } & Record<string, unknown>;
    help_text?: string | null;
    show_when?: { field: string; equals: unknown } | null;
    sort_order?: number;
  };
  trigger: ReactNode;
}) {
  const write = useConfigWrite(tenant);
  const [key, setKey] = useState(existing?.key ?? '');
  const [label, setLabel] = useState(existing?.label ?? '');
  const [fieldType, setFieldType] = useState(existing?.field_type ?? 'text');
  const [requiredAt, setRequiredAt] = useState(existing?.required_at ?? 'never');
  const [visibility, setVisibility] = useState(existing?.visibility ?? 'agent');
  const [whenMissing, setWhenMissing] = useState(existing?.when_missing_by_email ?? 'leave');
  const [consumer, setConsumer] = useState(existing?.consumer ?? '');
  // The saved options, one per line. This started empty, so an existing
  // select field looked as though it had none -- and saving it as a new
  // version wrote exactly that.
  const [options, setOptions] = useState((existing?.validation?.options ?? []).join('\n'));
  const [types, setTypes] = useState<string>(
    existing?.request_type_keys?.join(', ') ?? '',
  );
  const keyOk = /^[a-z][a-z0-9_]{1,40}$/.test(key);
  const needsOptions = fieldType === 'select' || fieldType === 'multiselect';

  return (
    <FormDialog
      trigger={trigger}
      title={existing ? `Field · ${existing.key} (v${existing.version})` : 'New field'}
      description={
        existing
          ? 'Saving creates a new version. Tickets raised under the current version keep it, so renaming an option cannot silently rewrite last year’s reporting.'
          : 'A field cannot be created without a stated consumer. That rule is what stops the next unused sub-category from being invented.'
      }
      submitLabel={existing ? 'Save as new version' : 'Create field'}
      pending={write.isPending}
      disabled={!keyOk || label.trim().length === 0 || consumer.trim().length < 3}
      width="lg"
      onSubmit={() =>
        write.mutateAsync({
          method: 'post',
          path: `/api/v1/tenants/${tenant}/admin/fields`,
          body: fieldSaveBody(existing, {
            key,
            label,
            fieldType,
            requiredAt,
            visibility,
            whenMissing,
            consumer,
            options,
            types,
          }),
          done: existing ? `"${label}" saved as a new version` : `Field "${label}" created`,
        })
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Key"
          error={key && !keyOk ? 'Lowercase letters, digits and underscores only' : undefined}
          required
        >
          <Input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={Boolean(existing)}
            placeholder="cost_code"
          />
        </Field>
        <Field label="Label" required>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Cost code" />
        </Field>
        <Field label="Type" required>
          <Select
            value={fieldType}
            onValueChange={setFieldType}
            options={FIELD_TYPES.map((t) => ({ value: t, label: t }))}
            aria-label="Field type"
          />
        </Field>
        <Field label="Required" hint="When the field must be filled in">
          <Select
            value={requiredAt}
            onValueChange={setRequiredAt}
            options={[
              { value: 'never', label: 'Never', hint: 'Optional everywhere' },
              { value: 'create', label: 'To raise the ticket' },
              { value: 'close', label: 'To close the ticket' },
            ]}
            aria-label="Required at"
          />
        </Field>
        <Field label="Who can set it">
          <Select
            value={visibility}
            onValueChange={setVisibility}
            options={[
              { value: 'agent', label: 'Agents only' },
              { value: 'requester', label: 'Requester can set it' },
            ]}
            aria-label="Visibility"
          />
        </Field>
        <Field
          label="When a ticket comes in by email without it"
          hint="An email never fails for a missing field. The desk still fills a required one in before closing."
        >
          <Select
            value={whenMissing}
            onValueChange={setWhenMissing}
            options={[
              { value: 'leave', label: 'Leave it for the desk' },
              { value: 'ask', label: 'Ask the requester', hint: 'The acknowledgement asks them for it' },
            ]}
            aria-label="When a ticket comes in by email without it"
          />
        </Field>
        <Field
          label="Only for these request types"
          hint="Comma separated keys. Blank means every type."
        >
          <Input value={types} onChange={(e) => setTypes(e.target.value)} placeholder="onboarding, offboarding" />
        </Field>
      </div>

      {needsOptions && (
        <Field label="Options" hint="One per line" required>
          <Textarea
            rows={4}
            value={options}
            onChange={(e) => setOptions(e.target.value)}
            placeholder={'Sacramento\nSan Francisco\nTampa'}
          />
        </Field>
      )}

      <Field
        label="Consumer"
        hint="Name the report, routing rule or automation that reads this field (FR-CF-06)"
        required
        error={consumer.length > 0 && consumer.trim().length < 3 ? 'Say what reads it' : undefined}
      >
        <Input
          value={consumer}
          onChange={(e) => setConsumer(e.target.value)}
          placeholder="the monthly recharge report"
        />
      </Field>

      {config && config.requestTypes.length > 0 && types.trim().length > 0 && (
        <p className="text-2xs text-ink-tertiary">
          Known types: {config.requestTypes.map((t) => t.key).join(', ')}
        </p>
      )}
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// SLA policies
// ---------------------------------------------------------------------------

export function SlaForm({
  tenant,
  config,
  existing,
  trigger,
}: {
  tenant: string;
  config: TenantConfig | undefined;
  existing?: {
    key: string;
    name: string;
    requestTypeKey: string | null;
    calendarKey: string;
    firstResponseMins: number;
    resolutionMins: number;
    atRiskPct: number;
  };
  trigger: ReactNode;
}) {
  const write = useConfigWrite(tenant);
  const [key, setKey] = useState(existing?.key ?? '');
  const [name, setName] = useState(existing?.name ?? '');
  const [requestTypeKey, setRequestTypeKey] = useState(existing?.requestTypeKey ?? '');
  const [calendarKey, setCalendarKey] = useState(
    existing?.calendarKey ?? config?.calendars[0]?.key ?? 'standard',
  );
  const [firstResponseHrs, setFirstResponseHrs] = useState(
    String((existing?.firstResponseMins ?? 120) / 60),
  );
  const [resolutionHrs, setResolutionHrs] = useState(
    String((existing?.resolutionMins ?? 960) / 60),
  );
  const [atRiskPct, setAtRiskPct] = useState(String(existing?.atRiskPct ?? 80));
  const keyOk = /^[a-z][a-z0-9_]{1,40}$/.test(key);
  const frMins = Math.round(num(firstResponseHrs) * 60);
  const resMins = Math.round(num(resolutionHrs) * 60);

  return (
    <FormDialog
      trigger={trigger}
      title={existing ? `SLA · ${existing.name}` : 'New SLA policy'}
      description="Targets are consumed in business hours on the chosen calendar, and the clock pauses whenever the ticket is waiting on somebody else."
      submitLabel={existing ? 'Save policy' : 'Create policy'}
      pending={write.isPending}
      disabled={!keyOk || name.trim().length === 0 || frMins < 1 || resMins < 1}
      onSubmit={() =>
        write.mutateAsync({
          method: 'post',
          path: `/api/v1/tenants/${tenant}/admin/sla`,
          body: {
            key,
            name: name.trim(),
            requestTypeKey: requestTypeKey || null,
            calendarKey,
            firstResponseMins: frMins,
            resolutionMins: resMins,
            atRiskPct: num(atRiskPct, 80),
          },
          done: `SLA "${name}" saved`,
        })
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field
          label="Key"
          error={key && !keyOk ? 'Lowercase letters, digits and underscores only' : undefined}
          required
        >
          <Input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            disabled={Boolean(existing)}
            placeholder="default"
          />
        </Field>
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Standard" />
        </Field>
        <Field
          label="Applies to"
          hint="One policy per request type, plus one default for everything else"
        >
          <Select
            value={requestTypeKey}
            onValueChange={setRequestTypeKey}
            placeholder="Everything else (default)"
            options={[
              { value: '', label: 'Everything else (default)' },
              ...(config?.requestTypes ?? []).map((t) => ({ value: t.key, label: t.label })),
            ]}
            aria-label="Applies to"
          />
        </Field>
        <Field label="Calendar">
          <Select
            value={calendarKey}
            onValueChange={setCalendarKey}
            options={(config?.calendars ?? [{ key: 'standard', timezone: '', hours: [], holidays: [] }]).map(
              (c) => ({ value: c.key, label: c.key, hint: c.timezone }),
            )}
            aria-label="Calendar"
          />
        </Field>
        <Field label="First response" hint="Business hours" required>
          <Input
            type="number"
            step="0.5"
            min="0.5"
            value={firstResponseHrs}
            onChange={(e) => setFirstResponseHrs(e.target.value)}
            className="w-28"
          />
        </Field>
        <Field label="Resolution" hint="Business hours" required>
          <Input
            type="number"
            step="0.5"
            min="0.5"
            value={resolutionHrs}
            onChange={(e) => setResolutionHrs(e.target.value)}
            className="w-28"
          />
        </Field>
        <Field label="At risk at" hint="% of the target consumed">
          <Input
            type="number"
            min="1"
            max="99"
            value={atRiskPct}
            onChange={(e) => setAtRiskPct(e.target.value)}
            className="w-24"
          />
        </Field>
      </div>
      <p className="text-2xs leading-relaxed text-ink-tertiary">
        {frMins} minutes to first response, {resMins} minutes to resolve — on a nine-hour working
        day that is {(resMins / 540).toFixed(1)} working days.
      </p>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Business calendar
// ---------------------------------------------------------------------------

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function CalendarForm({
  tenant,
  existing,
  trigger,
}: {
  tenant: string;
  existing: {
    key: string;
    timezone: string;
    hours: { dow: number; start: string; end: string }[];
    holidays: string[];
  };
  trigger: ReactNode;
}) {
  const write = useConfigWrite(tenant);
  const [timezone, setTimezone] = useState(existing.timezone);
  const [rows, setRows] = useState(
    DOW.map((_, i) => {
      const w = existing.hours.find((h) => h.dow === i + 1);
      return { on: Boolean(w), start: w?.start ?? '08:00', end: w?.end ?? '17:00' };
    }),
  );
  const [holidays, setHolidays] = useState(existing.holidays.join('\n'));

  const badRow = rows.findIndex((r) => r.on && r.end <= r.start);

  return (
    <FormDialog
      trigger={trigger}
      title={`Business hours · ${existing.key}`}
      description="This is what every SLA target on this calendar is measured against. A ticket that arrives at 6pm starts its clock at the next opening."
      submitLabel="Save calendar"
      pending={write.isPending}
      disabled={badRow !== -1}
      width="lg"
      onSubmit={() =>
        write.mutateAsync({
          method: 'post',
          path: `/api/v1/tenants/${tenant}/admin/calendars`,
          body: {
            key: existing.key,
            timezone,
            hours: rows
              .map((r, i) => ({ dow: i + 1, start: r.start, end: r.end, on: r.on }))
              .filter((r) => r.on)
              .map(({ dow, start, end }) => ({ dow, start, end })),
            holidays: holidays
              .split('\n')
              .map((h) => h.trim())
              .filter(Boolean),
          },
          done: 'Business hours saved',
        })
      }
    >
      <Field label="Timezone" hint="An IANA zone — the desk's local time, not the server's">
        <Input value={timezone} onChange={(e) => setTimezone(e.target.value)} />
      </Field>

      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-24 shrink-0">
              <Switch
                checked={r.on}
                onCheckedChange={(on) =>
                  setRows((prev) => prev.map((p, j) => (j === i ? { ...p, on } : p)))
                }
                label={DOW[i]}
              />
            </span>
            <Input
              type="time"
              value={r.start}
              disabled={!r.on}
              onChange={(e) =>
                setRows((prev) => prev.map((p, j) => (j === i ? { ...p, start: e.target.value } : p)))
              }
              className="w-28"
              aria-label={`${DOW[i]} opens`}
            />
            <span className="text-2xs text-ink-tertiary">to</span>
            <Input
              type="time"
              value={r.end}
              disabled={!r.on}
              onChange={(e) =>
                setRows((prev) => prev.map((p, j) => (j === i ? { ...p, end: e.target.value } : p)))
              }
              className="w-28"
              aria-label={`${DOW[i]} closes`}
            />
          </div>
        ))}
      </div>
      {badRow !== -1 && (
        <Callout tone="danger" icon={<AlertTriangle />}>
          {DOW[badRow]} closes at or before it opens.
        </Callout>
      )}

      <Field label="Holidays" hint="One ISO date per line. Observed dates, not nominal ones.">
        <Textarea
          rows={5}
          value={holidays}
          onChange={(e) => setHolidays(e.target.value)}
          placeholder="2026-11-26"
          className="font-mono"
        />
      </Field>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Routing rules
// ---------------------------------------------------------------------------

const RULE_FIELDS = [
  'request_type',
  'systems',
  'sender_email',
  'sender_domain',
  'requester_office',
  'requester_department',
  'template',
  'subject',
  'body',
  'project_number_present',
] as const;

const RULE_OPS = ['eq', 'neq', 'in', 'contains', 'matches', 'present', 'absent'] as const;

type Condition = { field: string; op: string; value: string };

export function RuleForm({
  tenant,
  config,
  existing,
  trigger,
  nextSortOrder,
}: {
  tenant: string;
  config: TenantConfig | undefined;
  existing?: {
    id: string;
    name: string;
    sort_order: number;
    predicate: { all: { field: string; op: string; value?: unknown }[] };
    action: Record<string, unknown>;
  };
  trigger: ReactNode;
  nextSortOrder: number;
}) {
  const write = useConfigWrite(tenant);
  const [name, setName] = useState(existing?.name ?? '');
  const [sortOrder, setSortOrder] = useState(String(existing?.sort_order ?? nextSortOrder));
  const [conditions, setConditions] = useState<Condition[]>(
    existing?.predicate.all.map((c) => ({
      field: c.field,
      op: c.op,
      value:
        c.value === undefined || c.value === null
          ? ''
          : Array.isArray(c.value)
            ? c.value.join(', ')
            : String(c.value),
    })) ?? [{ field: 'subject', op: 'contains', value: '' }],
  );
  const [queueKey, setQueueKey] = useState(String(existing?.action.queueKey ?? ''));
  const [flagForHuman, setFlagForHuman] = useState(Boolean(existing?.action.flagForHuman));
  // How it is handled (075).
  const [priority, setPriority] = useState(String(existing?.action.priority ?? ''));
  const [holdForPerson, setHoldForPerson] = useState(Boolean(existing?.action.holdForPerson));
  const [keepOpen, setKeepOpen] = useState(Boolean(existing?.action.keepOpen));
  const [notify, setNotify] = useState(
    Array.isArray(existing?.action.notify) ? (existing!.action.notify as string[]).join(', ') : '',
  );
  const notifyList = notify.split(/[,;\s]+/).map((v) => v.trim()).filter(Boolean);
  const badNotify = notifyList.filter((e) => !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e));
  const types = config?.requestTypes ?? [];

  /**
   * The action as saved. Keys this form does not edit (an assignee or field
   * values set through the API) are kept, so saving here cannot drop them.
   */
  const buildAction = () => {
    const kept = Object.fromEntries(
      Object.entries(existing?.action ?? {}).filter(
        ([k]) => !['queueKey', 'flagForHuman', 'priority', 'holdForPerson', 'keepOpen', 'notify'].includes(k),
      ),
    );
    return {
      ...kept,
      ...(queueKey ? { queueKey } : {}),
      ...(flagForHuman ? { flagForHuman: true } : {}),
      ...(priority ? { priority } : {}),
      ...(holdForPerson ? { holdForPerson: true } : {}),
      ...(keepOpen ? { keepOpen: true } : {}),
      ...(notifyList.length ? { notify: notifyList } : {}),
    };
  };

  const valueless = (op: string) => op === 'present' || op === 'absent';
  const incomplete = conditions.some((c) => !valueless(c.op) && c.value.trim().length === 0);

  const buildPredicate = () => ({
    all: conditions.map((c) => {
      if (valueless(c.op)) return { field: c.field, op: c.op };
      return {
        field: c.field,
        op: c.op,
        value: c.op === 'in' ? c.value.split(',').map((v) => v.trim()).filter(Boolean) : c.value,
      };
    }),
  });

  return (
    <FormDialog
      trigger={trigger}
      title={existing ? `Rule · ${existing.name}` : 'New routing rule'}
      description="Conditions are joined with AND; for OR, write two rules. Where a ticket goes: the first matching rule decides. How it is handled (priority, the assistant, closing, alerts): every matching rule counts, and the strictest wins."
      submitLabel={existing ? 'Save rule' : 'Create rule'}
      pending={write.isPending}
      disabled={name.trim().length === 0 || incomplete || badNotify.length > 0}
      width="lg"
      onSubmit={() =>
        write.mutateAsync(
          existing
            ? {
                method: 'patch',
                path: `/api/v1/tenants/${tenant}/admin/rules/${existing.id}`,
                body: {
                  name: name.trim(),
                  sortOrder: num(sortOrder),
                  predicate: buildPredicate(),
                  action: buildAction(),
                },
                done: `Rule "${name}" saved`,
              }
            : {
                method: 'post',
                path: `/api/v1/tenants/${tenant}/admin/rules`,
                body: {
                  name: name.trim(),
                  sortOrder: num(sortOrder),
                  predicate: buildPredicate(),
                  action: buildAction(),
                },
                done: `Rule "${name}" created`,
              },
        )
      }
    >
      <div className="grid gap-3 sm:grid-cols-[1fr_7rem]">
        <Field label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Phishing reports to Security"
          />
        </Field>
        <Field label="Order" hint="First match wins" required>
          <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
        </Field>
      </div>

      <div>
        <p className="mb-1.5 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">
          When all of these are true
        </p>
        <div className="space-y-1.5">
          {conditions.map((c, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <Select
                value={c.field}
                onValueChange={(v) =>
                  setConditions((prev) => prev.map((p, j) => (j === i ? { ...p, field: v } : p)))
                }
                options={RULE_FIELDS.map((f) => ({ value: f, label: f }))}
                className="w-52"
                aria-label="Field"
              />
              <Select
                value={c.op}
                onValueChange={(v) =>
                  setConditions((prev) => prev.map((p, j) => (j === i ? { ...p, op: v } : p)))
                }
                options={RULE_OPS.map((o) => ({ value: o, label: o }))}
                className="w-32"
                aria-label="Operator"
              />
              {c.field === 'request_type' && (c.op === 'eq' || c.op === 'neq') && types.length ? (
                // The ticket carries the type's label: picking it here means
                // the rule cannot be written with a key that never matches.
                <Select
                  value={c.value}
                  onValueChange={(v) =>
                    setConditions((prev) => prev.map((p, j) => (j === i ? { ...p, value: v } : p)))
                  }
                  placeholder="Choose a request type"
                  options={types.map((t) => ({ value: t.label, label: t.label }))}
                  className="min-w-0 flex-1"
                  aria-label="Request type"
                />
              ) : (
                <Input
                  value={c.value}
                  disabled={valueless(c.op)}
                  placeholder={valueless(c.op) ? 'no value needed' : c.op === 'in' ? 'a, b, c' : 'value'}
                  onChange={(e) =>
                    setConditions((prev) =>
                      prev.map((p, j) => (j === i ? { ...p, value: e.target.value } : p)),
                    )
                  }
                />
              )}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={conditions.length === 1}
                onClick={() => setConditions((prev) => prev.filter((_, j) => j !== i))}
                aria-label="Remove condition"
              >
                &times;
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="mt-1.5"
          onClick={() =>
            setConditions((prev) => [...prev, { field: 'subject', op: 'contains', value: '' }])
          }
        >
          <Plus aria-hidden className="h-3.5 w-3.5" />
          Add condition
        </Button>
      </div>

      <div className="rounded-md border border-line-subtle bg-surface-sunken/40 p-3">
        <p className="mb-2 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">Then</p>
        <div className="space-y-2.5">
          <Field label="Send to queue" horizontal>
            <Select
              value={queueKey}
              onValueChange={setQueueKey}
              placeholder="Leave the queue alone"
              options={[
                { value: '', label: 'Leave the queue alone' },
                ...(config?.queues ?? []).map((q) => ({ value: q.key, label: q.name })),
              ]}
              className="w-56"
              aria-label="Queue"
            />
          </Field>
          <Switch
            checked={flagForHuman}
            onCheckedChange={setFlagForHuman}
            label="Flag for a human to check"
          />
        </div>
      </div>

      <div className="rounded-md border border-line-subtle bg-surface-sunken/40 p-3">
        <p className="mb-1 text-2xs font-medium uppercase tracking-wide text-ink-tertiary">And handle it like this</p>
        <p className="mb-2.5 text-2xs leading-relaxed text-ink-tertiary">
          For requests that must reach a person, such as security reports. Applied to new tickets from
          every source, and by Re-run routing.
        </p>
        <div className="space-y-2.5">
          <Field label="Priority" horizontal>
            <Select
              value={priority}
              onValueChange={setPriority}
              placeholder="Leave the priority alone"
              options={[
                { value: '', label: 'Leave the priority alone' },
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'critical', label: 'Critical' },
              ]}
              className="w-56"
              aria-label="Priority"
            />
          </Field>
          <Switch
            checked={holdForPerson}
            onCheckedChange={setHoldForPerson}
            label="Keep the assistant out: acknowledge it and leave it for a person"
          />
          <Switch
            checked={keepOpen}
            onCheckedChange={setKeepOpen}
            label="Never close by itself: a person closes it, not auto-close or a requester's 'done'"
          />
          <Field
            label="Alert by email"
            hint="People or a distribution list, separated by commas. Sent when the ticket arrives."
            error={badNotify.length ? `Not an email address: ${badNotify.join(', ')}` : undefined}
          >
            <Input
              value={notify}
              onChange={(e) => setNotify(e.target.value)}
              placeholder="security@esassoc.com"
            />
          </Field>
        </div>
      </div>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * The settings dialog's fields, from what the desk has saved.
 *
 * ONE PLACE, CALLED ON EVERY OPEN. The fields used to be filled once, when
 * the Configuration page first rendered — which on a fresh load is before
 * the desk's settings have arrived. Every field then showed its default
 * (opt-in off, auto-close 5, threshold 90%) whatever was saved, and Save
 * wrote those defaults back over the real values. Filling them as the dialog
 * opens means they are always what is saved at that moment.
 */
export function settingsFields(config: Pick<TenantConfig, 'settings' | 'requestTypes'> | undefined) {
  const s = config?.settings;
  // Stored as whatever names the type; shown as the label it resolves to.
  const securityNow = s?.securityReportType ?? '';
  return {
    autoCloseDays: String(s?.autoCloseDays ?? 5),
    reopenWindowDays: String(s?.reopenWindowDays ?? 14),
    threshold: String(Math.round((s?.autoRouteThreshold ?? 0.9) * 100)),
    showSla: Boolean(s?.showSlaToRequester),
    onBehalf: s?.allowOnBehalfOf ?? true,
    assistantIntake: Boolean(s?.assistantIntake),
    securityNow,
    securityType:
      config?.requestTypes.find((t) => t.key === securityNow || t.label === securityNow)?.key ?? (securityNow ? '__other' : ''),
  };
}

export function SettingsForm({
  tenant,
  config,
  trigger,
  requestsOnly = false,
}: {
  tenant: string;
  config: TenantConfig | undefined;
  trigger: ReactNode;
  /** A Request manager: only the settings about how requests are handled. */
  requestsOnly?: boolean;
}) {
  const write = useConfigWrite(tenant);
  const initial = settingsFields(config);
  const [autoCloseDays, setAutoCloseDays] = useState(initial.autoCloseDays);
  const [reopenWindowDays, setReopenWindowDays] = useState(initial.reopenWindowDays);
  const [threshold, setThreshold] = useState(initial.threshold);
  const [showSla, setShowSla] = useState(initial.showSla);
  const [onBehalf, setOnBehalf] = useState(initial.onBehalf);
  const [assistantIntake, setAssistantIntake] = useState(initial.assistantIntake);
  const [securityType, setSecurityType] = useState(initial.securityType);
  const securityNow = initial.securityNow;
  const fill = () => {
    const f = settingsFields(config);
    setAutoCloseDays(f.autoCloseDays);
    setReopenWindowDays(f.reopenWindowDays);
    setThreshold(f.threshold);
    setShowSla(f.showSla);
    setOnBehalf(f.onBehalf);
    setAssistantIntake(f.assistantIntake);
    setSecurityType(f.securityType);
  };
  const pct = num(threshold, 90);

  return (
    <FormDialog
      trigger={trigger}
      title="Tenant settings"
      description="The per-tenant knobs. Each one is a decision this team gets to make for itself."
      submitLabel="Save settings"
      pending={write.isPending}
      onOpen={fill}
      // Never saveable before the settings it would overwrite have loaded.
      disabled={!config || pct < 50 || pct > 100}
      onSubmit={() =>
        write.mutateAsync({
          method: 'patch',
          path: `/api/v1/tenants/${tenant}/admin/settings`,
          body: {
            autoCloseDays: num(autoCloseDays, 5),
            reopenWindowDays: num(reopenWindowDays, 14),
            autoRouteThreshold: pct / 100,
            showSlaToRequester: showSla,
            // '__other' is a saved name matching no current type: left as it was.
            ...(securityType === '__other' ? {} : { securityReportType: securityType }),
            ...(requestsOnly ? {} : { allowOnBehalfOf: onBehalf, assistantIntake }),
          },
          done: 'Settings saved',
        })
      }
    >
      <Field label="Auto-close resolved tickets after" hint="Days" required>
        <Input
          type="number"
          min="1"
          max="90"
          value={autoCloseDays}
          onChange={(e) => setAutoCloseDays(e.target.value)}
          className="w-24"
        />
      </Field>
      <Field label="Requesters can reopen for" hint="Days after closure" required>
        <Input
          type="number"
          min="1"
          max="365"
          value={reopenWindowDays}
          onChange={(e) => setReopenWindowDays(e.target.value)}
          className="w-24"
        />
      </Field>
      <Field
        label="Auto-route confidence threshold"
        hint="Below this the router refuses to guess and asks a person instead"
        error={pct < 50 || pct > 100 ? 'Between 50 and 100' : undefined}
        required
      >
        <Input
          type="number"
          min="50"
          max="100"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          className="w-24"
        />
      </Field>
      <Field
        label="Security reports"
        hint="The request type that means someone reported a suspicious email, call or link. Reports of it from several people are grouped into one campaign (Reports, Security)."
      >
        <Select
          value={securityType}
          onValueChange={setSecurityType}
          options={[
            { value: '', label: 'None: campaign detection off' },
            ...(securityType === '__other' ? [{ value: '__other', label: `${securityNow} (no such type now)` }] : []),
            ...(config?.requestTypes ?? []).map((t) => ({ value: t.key, label: t.label })),
          ]}
          className="w-72"
          aria-label="Security report type"
        />
      </Field>
      <Switch checked={showSla} onCheckedChange={setShowSla} label="Show SLA targets to requesters" />
      {!requestsOnly && (
        <Switch
          checked={onBehalf}
          onCheckedChange={setOnBehalf}
          label="Allow raising a ticket on somebody else's behalf"
        />
      )}
      {!requestsOnly && (
        <div className="space-y-1">
          <Switch
            checked={assistantIntake}
            onCheckedChange={setAssistantIntake}
            label="Take requests from AI agents"
          />
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            Agents connected to Tributary — the Teams bot, Claude, or any other assistant using the intake key — may send
            this desk the questions people ask them. It answers from this desk's knowledgebase and files the ticket here
            for the person, using this desk's Intake settings. Tributary decides which desk a question is for from what
            each says it handles under Intake, so write that first.
          </p>
        </div>
      )}
      <Callout tone="neutral">
        The confidence threshold is open decision #4 in the requirements. {pct}% is a starting
        value, not an answer — the routing tab can replay a change against historic tickets before
        it goes live.
      </Callout>
    </FormDialog>
  );
}

// ---------------------------------------------------------------------------
// Access grants
// ---------------------------------------------------------------------------

export function GrantForm({
  tenant,
  trigger,
  mayGrantPlatformAdmin,
  maxRole,
  extras = [],
}: {
  tenant: string;
  trigger: ReactNode;
  mayGrantPlatformAdmin: boolean;
  /** A delegate (Request manager) grants no higher than their own role. */
  maxRole?: TenantRole;
  /**
   * Grants held alongside the role (Request manager, Knowledge reviewer)
   * that this person may hand out, offered in the same dialog so giving
   * someone a role and a grant is one step.
   */
  extras?: { key: string; label: string; hint: string }[];
}) {
  const write = useConfigWrite(tenant);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('agent');
  const [reason, setReason] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  // Every extra grant needs at least agent; a requester cannot hold one.
  const extrasApply = role !== 'requester';
  const emailOk = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);

  return (
    <FormDialog
      trigger={trigger}
      title="Grant access"
      description="The person must already exist as an ESA identity. Grants are stored data with an explicit revocation, not a config file."
      submitLabel="Grant"
      pending={write.isPending}
      disabled={!emailOk}
      onSubmit={async () => {
        await write.mutateAsync({
          method: 'post',
          path: `/api/v1/tenants/${tenant}/admin/roles`,
          body: { email: email.trim(), role, reason: reason.trim() || undefined },
          done: `${email} is now ${role.replace('_', ' ')}`,
        });
        // After the role: each grant needs it in place first.
        for (const key of extrasApply ? chosen : []) {
          const x = extras.find((e) => e.key === key);
          await write.mutateAsync({
            method: 'post',
            path: `/api/v1/tenants/${tenant}/admin/extra-grants`,
            body: { email: email.trim(), grant: key },
            done: `${email} is now a ${x?.label.toLowerCase() ?? key}`,
          });
        }
        setChosen([]);
      }}
    >
      <Field
        label="Email"
        required
        error={email.length > 0 && !emailOk ? 'Not an email address' : undefined}
      >
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="someone@esassoc.com"
        />
      </Field>
      <Field label="Role" required>
        <Select
          value={role}
          onValueChange={setRole}
          options={[
            { value: 'requester', label: 'Requester', hint: 'Can raise and track their own' },
            { value: 'agent', label: 'Agent', hint: 'Works the queue' },
            { value: 'queue_lead', label: 'Queue lead', hint: 'Agent, plus the dashboards' },
            { value: 'tenant_admin', label: 'Tenant admin', hint: 'Can change this configuration' },
            ...(mayGrantPlatformAdmin && !maxRole
              ? [{ value: 'platform_admin', label: 'Platform admin', hint: 'Every tenant' }]
              : []),
          ].filter((o) => !maxRole || rankOf(o.value as TenantRole) <= rankOf(maxRole))}
          aria-label="Role"
        />
      </Field>
      {extras.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-2xs font-medium text-ink-secondary">Also</p>
          {extras.map((x) => (
            <div key={x.key}>
              <Switch
                checked={extrasApply && chosen.includes(x.key)}
                disabled={!extrasApply}
                onCheckedChange={(on) =>
                  setChosen((cur) => (on ? [...cur, x.key] : cur.filter((k) => k !== x.key)))
                }
                label={x.label}
              />
              <p className="ml-11 text-2xs text-ink-tertiary">{x.hint}</p>
            </div>
          ))}
          {!extrasApply && <p className="text-2xs text-ink-tertiary">These need at least the agent role.</p>}
        </div>
      )}
      <Field label="Reason" hint="Recorded against the grant">
        <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Joining the service desk" />
      </Field>
      {maxRole && (
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          You can grant roles up to your own ({maxRole.replace('_', ' ')}). Anything higher needs a
          tenant admin.
        </p>
      )}
      {!mayGrantPlatformAdmin && !maxRole && (
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          A tenant admin cannot mint a platform admin — that would be a privilege escalation, and
          the API refuses it regardless of what this form offers.
        </p>
      )}
    </FormDialog>
  );
}
